import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import capitalize from 'lodash/capitalize'
import React, { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'

import AddressLatLong from 'components/common/address/AddressLatLong'
import AddressRowOne from 'components/common/address/AddressRowOne'
import AddressRowTwo from 'components/common/address/AddressRowTwo'
import { DEFAULT_STATE } from 'components/common/address/model'
import { HHFormTextField, HHFormCheckboxField } from 'components/form-fields/v5'
import { useBillingAddressFormFields } from 'components/global-actions/new-customer/useBillingAddressFormFields'
import { DS_OVERLINE_SX } from 'components/taxes/common/dsTypography'
import { DRAWER_POPPER_SX } from 'components/taxes/tax-drawer/taxDrawerForm'
import T from 'T'
import { getAddressParams } from 'utils/helper'

import type { NewAccountFormValues } from './model'
import type { LatLng, LatLngHistory } from './useNewAccountMap'

const { addressNamePlaceholder } = DEFAULT_STATE

type NewAccountLocationFieldsProps = {
  onKeyDown: (event: React.KeyboardEvent) => void
  setLatLngForMap: React.Dispatch<React.SetStateAction<LatLng>>
  setLatLngForMapHistory: React.Dispatch<React.SetStateAction<LatLngHistory>>
}

const NewAccountLocationFields = ({ onKeyDown, setLatLngForMap, setLatLngForMapHistory }: NewAccountLocationFieldsProps) => {
  const {
    control,
    watch,
    setValue,
    setFocus,
    formState: { errors },
  } = useFormContext<NewAccountFormValues>()
  const addressErrors = errors.address
  const watchLine1 = watch('address.line1')
  const watchCity = watch('address.city')
  const watchState = watch('address.state')
  const watchLong = watch('address.longitude')
  const watchLat = watch('address.latitude')

  const {
    watchUseAsBillingAddress,
    watchBillingLine1,
    watchBillingCity,
    watchBillingState,
    billingAddressErrors,
    handleBillingAddressFieldChange,
  } = useBillingAddressFormFields()

  const handleBillingAddressChange = useCallback(
    (address: unknown) => {
      const { line1, city, state, zipCode, country, longitudeFixed, latitudeFixed } = getAddressParams(address)
      setValue('address.line1', line1, { shouldDirty: true })
      setValue('address.city', city, { shouldDirty: true })
      setValue('address.state', state, { shouldDirty: true })
      setValue('address.country', country, { shouldDirty: true })
      setValue('address.zipCode', zipCode, { shouldDirty: true })
      setValue('address.latitude', latitudeFixed, { shouldDirty: true })
      setValue('address.longitude', longitudeFixed, { shouldDirty: true })
      setLatLngForMap({ longitudeForMap: longitudeFixed, latitudeForMap: latitudeFixed })
      setLatLngForMapHistory({ longitudeForMapHistory: longitudeFixed, latitudeForMapHistory: latitudeFixed })
      setFocus('accountName')
    },
    [setValue, setLatLngForMap, setLatLngForMapHistory, setFocus]
  )

  const handleLine1Change = useCallback((newValue: string) => setValue('address.line1', newValue, { shouldDirty: true }), [setValue])

  const handleBillingLine1Change = useCallback(
    (newValue: string) => setValue('billingAddress.line1', newValue, { shouldDirty: true }),
    [setValue]
  )

  const handleBlurLat = useCallback(
    (value: string) => {
      setLatLngForMap(prev => ({ ...prev, latitudeForMap: value }))
      setLatLngForMapHistory(prev => ({ ...prev, latitudeForMapHistory: value }))
    },
    [setLatLngForMap, setLatLngForMapHistory]
  )

  const handleBlurLong = useCallback(
    (value: string) => {
      setLatLngForMap(prev => ({ ...prev, longitudeForMap: value }))
      setLatLngForMapHistory(prev => ({ ...prev, longitudeForMapHistory: value }))
    },
    [setLatLngForMap, setLatLngForMapHistory]
  )

  return (
    <>
      <Grid size={12}>
        <Divider sx={{ mb: 2 }} />
        <Typography color="text.secondary" sx={DS_OVERLINE_SX} variant="subtitle2">
          {T.SERVICE_LOCATION}
        </Typography>
      </Grid>
      <Grid size={12}>
        <HHFormTextField
          fullWidth
          control={control}
          deprecatedLabel={false}
          label={capitalize(T.SERVICE_LOCATION_NAME)}
          name="address.addressName"
          placeholder={addressNamePlaceholder}
          onKeyDown={onKeyDown}
        />
      </Grid>
      <AddressRowOne
        line1Required
        autocompleteSlotProps={{ popper: { sx: DRAWER_POPPER_SX } }}
        control={control}
        hasError={{ line1: watchLine1 ? undefined : addressErrors?.line1 }}
        line1={watchLine1}
        line1Label={T.ADDRESS}
        onBillingAddressChange={handleBillingAddressChange}
        onKeyDown={onKeyDown}
        onLine1Change={handleLine1Change}
      />
      <AddressRowTwo
        required
        control={control}
        hasError={{
          city: watchCity ? undefined : addressErrors?.city,
          state: watchState ? undefined : addressErrors?.state,
        }}
        onKeyDown={onKeyDown}
      />
      <AddressLatLong
        control={control}
        hasLatError={Boolean(addressErrors?.latitude)}
        hasLongError={Boolean(addressErrors?.longitude)}
        lat={watchLat}
        long={watchLong}
        onBlurLat={handleBlurLat}
        onBlurLong={handleBlurLong}
      />
      <Grid size={12}>
        <FormControlLabel
          control={<HHFormCheckboxField control={control} name="useAsBillingAddress" />}
          label={T.USE_SERVICE_ADDRESS_AS_BILLING_ADDRESS}
        />
      </Grid>
      <Collapse unmountOnExit in={!watchUseAsBillingAddress}>
        <Grid container rowGap={1.5} sx={{ width: '100%' }}>
          <Grid size={12}>
            <Typography color="text.secondary" sx={DS_OVERLINE_SX} variant="subtitle2">
              {T.BILLING_ADDRESS}
            </Typography>
          </Grid>
          <AddressRowOne
            line1Required
            autocompleteSlotProps={{ popper: { sx: DRAWER_POPPER_SX } }}
            control={control}
            fieldPrefix="billingAddress"
            hasError={{ line1: billingAddressErrors?.line1 }}
            line1={watchBillingLine1}
            line1Label={T.ADDRESS}
            onBillingAddressChange={handleBillingAddressFieldChange}
            onKeyDown={onKeyDown}
            onLine1Change={handleBillingLine1Change}
          />
          <AddressRowTwo
            required
            control={control}
            fieldPrefix="billingAddress"
            hasError={{
              city: watchBillingCity ? undefined : billingAddressErrors?.city,
              state: watchBillingState ? undefined : billingAddressErrors?.state,
            }}
            onKeyDown={onKeyDown}
          />
        </Grid>
      </Collapse>
    </>
  )
}

export default NewAccountLocationFields
